import React, { useState } from 'react';
import { Globe2, Menu, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';

interface HeaderProps {
  onAuthClick: () => void;
}

export default function Header({ onAuthClick }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, profile, signOut } = useAuthStore();
  const [showMenu, setShowMenu] = useState(false);

  const handleLanguageChange = async (lang: string) => {
    // Update i18n language
    i18n.changeLanguage(lang);

    // Update user profile in Supabase if logged in
    if (user && profile) {
      await supabase
        .from('profiles')
        .update({ language: lang })
        .eq('id', user.id);
    }
  };

  const languages = {
    en: t('languages.english'),
    es: t('languages.spanish'),
    fr: t('languages.french'),
    de: t('languages.german'),
    it: t('languages.italian'),
    pt: t('languages.portuguese'),
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowMenu(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="bg-white shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Globe2 className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">
                {t('common.welcome')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <select 
              value={profile?.language || i18n.language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              aria-label={t('header.language')}
            >
              {Object.entries(languages).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>

            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                aria-label={t('header.menu')}
              >
                {user ? (
                  <User className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>

              {showMenu && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100">
                  {user ? (
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700">
                        {profile?.email}
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('common.signOut')}
                      </button>
                    </div>
                  ) : (
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onAuthClick();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('common.signIn')}
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onAuthClick();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('common.signUp')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}